import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { films } from "@/mocks/films";
import {
  filmCategoryMeta,
  genreOptions,
  regionOptions,
  yearOptions,
} from "@/mocks/categories";
import type { FilmCategory } from "@/types";
import { useSEO } from "@/hooks/useSEO";
import FilmCard from "@/components/base/FilmCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";

const PAGE_SIZE = 4;
const validTypes: FilmCategory[] = ["movie", "tv", "variety"];

type SortKey = "latest" | "popular";

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const category = (validTypes.includes(type as FilmCategory) ? type : "movie") as FilmCategory;
  const meta = filmCategoryMeta[category];

  const [searchParams, setSearchParams] = useSearchParams();
  const [genres, setGenres] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [years, setYears] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>("latest");
  const [filterOpen, setFilterOpen] = useState(false);

  const page = Number(searchParams.get("page") || "1");

  useSEO({
    title: `${meta.label}分类 - 星光传媒影视平台`,
    description: `${meta.intro} 查看星光传媒精选${meta.label}作品列表，支持题材、地区与年份筛选。`,
    keywords: `星光传媒,${meta.label},影视分类,${meta.label}推荐,影视作品`,
  });

  const toggle = (list: string[], value: string, setter: (v: string[]) => void) => {
    setter(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
    setSearchParams({ page: "1" });
  };

  const resetAll = () => {
    setGenres([]);
    setRegions([]);
    setYears([]);
    setSort("latest");
    setSearchParams({ page: "1" });
  };

  const filtered = useMemo(() => {
    const list = films.filter((film) => {
      if (film.category !== category) return false;
      if (genres.length && !film.genres.some((g) => genres.includes(g))) return false;
      if (regions.length && !regions.includes(film.region)) return false;
      if (years.length && !years.includes(String(film.year))) return false;
      return true;
    });
    return list.sort((a, b) =>
      sort === "latest"
        ? b.year - a.year || b.popularity - a.popularity
        : b.popularity - a.popularity || b.rating - a.rating
    );
  }, [category, genres, regions, years, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const goPage = (next: number) => {
    setSearchParams({ page: String(next) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const hasFilter = genres.length + regions.length + years.length > 0;

  const FilterGroup = ({
    title,
    options,
    selected,
    onToggle,
  }: {
    title: string;
    options: string[];
    selected: string[];
    onToggle: (value: string) => void;
  }) => (
    <div>
      <h3 className="mb-3 text-sm font-semibold text-foreground-900">{title}</h3>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = selected.includes(option);
          return (
            <button
              key={option}
              type="button"
              onClick={() => onToggle(option)}
              className={`rounded-md px-3 py-2 text-sm transition-colors ${
                active
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-800"
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );

  const filters = (
    <div className="flex flex-col gap-6">
      <FilterGroup
        title="题材类型"
        options={genreOptions}
        selected={genres}
        onToggle={(v) => toggle(genres, v, setGenres)}
      />
      <FilterGroup
        title="地区"
        options={regionOptions}
        selected={regions}
        onToggle={(v) => toggle(regions, v, setRegions)}
      />
      <FilterGroup
        title="年份"
        options={yearOptions}
        selected={years}
        onToggle={(v) => toggle(years, v, setYears)}
      />
      {hasFilter && (
        <button
          type="button"
          onClick={resetAll}
          className="flex items-center justify-center gap-1.5 rounded-md border border-background-300 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
        >
          <i className="ri-refresh-line" />
          重置全部筛选
        </button>
      )}
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: `${meta.label}分类` }]} />

      <header className="mt-4 border-b border-background-200 pb-6">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {meta.label}分类
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
          {meta.intro}
        </p>
      </header>

      <div className="mt-6 flex flex-col gap-8 lg:flex-row">
        {/* Sidebar filters (desktop) */}
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="mb-5 flex items-center justify-between">
              <span className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-filter-3-line text-accent-500" />
                筛选
              </span>
              {hasFilter && (
                <button
                  type="button"
                  onClick={resetAll}
                  className="text-xs text-foreground-500 hover:text-accent-600"
                >
                  清空
                </button>
              )}
            </div>
            {filters}
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          {/* Toolbar */}
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setFilterOpen((prev) => !prev)}
                className="flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 lg:hidden"
              >
                <i className="ri-filter-3-line" />
                筛选
                {hasFilter && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-500 px-1 text-xs text-background-50">
                    {genres.length + regions.length + years.length}
                  </span>
                )}
              </button>
              <span className="text-sm text-foreground-500">
                共 <strong className="text-foreground-900">{filtered.length}</strong> 部作品
              </span>
            </div>
            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
              {(
                [
                  { key: "latest", label: "最新" },
                  { key: "popular", label: "热门" },
                ] as { key: SortKey; label: string }[]
              ).map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    setSort(item.key);
                    setSearchParams({ page: "1" });
                  }}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    sort === item.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile filter panel */}
          {filterOpen && (
            <div className="mb-5 rounded-lg border border-background-200 bg-background-50 p-5 lg:hidden">
              {filters}
            </div>
          )}

          {paged.length === 0 ? (
            <EmptyState
              icon="ri-movie-line"
              title="没有找到符合条件的作品"
              desc="试试减少筛选条件，或重置全部筛选，发现更多精彩内容。"
              action={
                <button
                  type="button"
                  onClick={resetAll}
                  className="whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
                >
                  重置筛选条件
                </button>
              }
            />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-5">
                {paged.map((film) => (
                  <FilmCard key={film.id} film={film} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-2">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => goPage(currentPage - 1)}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors enabled:hover:border-accent-400 enabled:hover:text-accent-600 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <i className="ri-arrow-left-s-line text-xl" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => goPage(num)}
                      className={`flex h-9 min-w-9 items-center justify-center rounded-md px-3 text-sm font-medium transition-colors ${
                        num === currentPage
                          ? "bg-primary-500 text-background-50"
                          : "border border-background-300 text-foreground-700 hover:border-accent-400 hover:text-accent-600"
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() => goPage(currentPage + 1)}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors enabled:hover:border-accent-400 enabled:hover:text-accent-600 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <i className="ri-arrow-right-s-line text-xl" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}